import React, { useState, useEffect } from 'react';
import '../common.scss';
import './styles.scss';
import Components from './components';
import { useCustomComponent } from '@/hooks';

export const DATE_FIELD_NOT_SURE_VALUE = "I'm not sure yet";

export interface DateProps {
  id?: string;
  name: string;
  placeholder?: string;
  unsureCheckboxText?: string;
  required?: boolean;
  disabled?: boolean;
  value?: string;
  min?: string;
  max?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const DateComponent: React.FC<DateProps> = ({
  id,
  name,
  value = '',
  unsureCheckboxText,
  required,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  ...props
}) => {
  const [isUnsure, setIsUnsure] = useState(false);
  const [dateValue, setDateValue] = useState('');

  // Initialize state based on value
  useEffect(() => {
    if (value === DATE_FIELD_NOT_SURE_VALUE) {
      setIsUnsure(true);
      setDateValue('');
    } else if (value) {
      setIsUnsure(false);
      setDateValue(value);
    } else {
      // Clear internal state when value prop is empty
      setIsUnsure(false);
      setDateValue('');
    }
  }, [value]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setDateValue(newDate);
    onChange?.(e);
  };

  const handleUnsureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsUnsure(checked);

    if (checked) {
      // Create a synthetic event for the "I'm not sure yet" value
      const syntheticEvent = {
        target: { value: DATE_FIELD_NOT_SURE_VALUE },
        currentTarget: { value: DATE_FIELD_NOT_SURE_VALUE },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    } else if (dateValue) {
      // Restore the specific date if one was previously selected
      const syntheticEvent = {
        target: { value: dateValue },
        currentTarget: { value: dateValue },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    } else {
      // Clear the value
      const syntheticEvent = {
        target: { value: '' },
        currentTarget: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    }
  };

  return (
    <Components.Container>
      <Components.Input
        {...props}
        id={id}
        name={name}
        value={isUnsure ? '' : dateValue}
        disabled={disabled || isUnsure}
        onChange={handleDateChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {required && unsureCheckboxText && (
        <Components.DateUnsureCheckbox
          checked={isUnsure}
          text={unsureCheckboxText}
          disabled={disabled}
          onChange={handleUnsureChange}
        />
      )}
    </Components.Container>
  );
};

export const Date: React.FC<DateProps> = (props: DateProps) => {
  const Component = useCustomComponent({
    componentName: 'Date',
    Component: DateComponent,
  });

  return <Component {...props} />;
};
